/**
 * Date formatting utilities — shared across mailbox-web, products-web, marketing-web.
 *
 * WHY THIS EXISTS
 * ───────────────
 * All timestamps are stored as UTC in SQL Server but .NET serializes DateTime
 * values without a 'Z' suffix when DateTimeKind is Unspecified. Browsers parse
 * strings without timezone info as *local* time in some environments and UTC in
 * others — causing silent display errors.
 *
 * These helpers always re-interpret the raw string as UTC before formatting, then
 * let the browser convert to the user's local timezone via toLocaleString().
 * A short timezone label (e.g. "EST", "PDT") is appended so users know the time
 * has been localised.
 *
 * STORAGE RULE: do NOT change how timestamps are stored — UTC is correct for
 * forensic integrity. These helpers only affect display.
 */

/** @internal Ensure a raw date value is interpreted as UTC. */
function toUtcDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const s = value.trim();
  if (!s) return null;

  // Already has timezone info (ends with Z, +HH:MM, -HH:MM, etc.)
  if (/Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  // No timezone — assume UTC (SQL Server datetime without 'Z')
  const d = new Date(s + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

// ── Public helpers ────────────────────────────────────────────────────────────

/**
 * Full date + time in the user's local timezone, with abbreviated TZ label.
 * e.g. "Mar 5, 2026, 9:30 AM EST"
 */
export function fmtDateTime(value: string | Date | null | undefined): string {
  const d = toUtcDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Date only in the user's local timezone.
 * e.g. "Mar 5, 2026"
 */
export function fmtDate(value: string | Date | null | undefined): string {
  const d = toUtcDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Compact date + time for tight spaces (message lists, table cells).
 * e.g. "Mar 5, 9:30 AM"  (no year, no TZ — year omitted when space is tight)
 */
export function fmtDateTimeShort(value: string | Date | null | undefined): string {
  const d = toUtcDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Month + year only.
 * e.g. "Mar 2026"
 */
export function fmtMonthYear(value: string | Date | null | undefined): string {
  const d = toUtcDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Time only in the user's local timezone.
 * e.g. "9:30 AM"
 */
export function fmtTime(value: string | Date | null | undefined): string {
  const d = toUtcDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
